import AwsS3Multipart from '@uppy/aws-s3-multipart';
import Uppy from '@uppy/core';
import { throttle } from 'lodash-es';
import { z } from 'zod';
import { gql } from '@soundxyz/gql-string';
import { IS_TEST } from '@soundxyz/utils/const';
import type { MediaType } from '@graphql/reactQuery';
import {
  CompleteMultipartUploadDocument,
  CreateMultipartUploadDocument,
  fetchGQL,
  SignedUploadParamsDocument,
} from '@graphql/reactQuery';
import { ApiDataError, FailedMediaUploadError } from '@utils/error';
import { MOCK_TEST_S3 } from './test';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function uploadMediaToS3(file: File, url: string, fields: any) {
  const formData = new FormData();

  Object.entries({ ...fields, file }).forEach(([key, value]) => {
    formData.append(key, value as string);
  });

  return fetch(url, {
    method: 'POST',
    body: formData,
  });
}

/*#__PURE__*/ gql(/* GraphQL */ `
  query signedUploadParams($uploadRequest: UploadRequest!) {
    signedUploadParams(uploadRequest: $uploadRequest) {
      uploadKey
      url
      fields
    }
  }

  mutation CreateMultipartUpload($input: MultipartUploadRequest!) {
    createMultipartUpload(input: $input) {
      __typename
      ... on AwsRequestError {
        requestId
        message
      }
      ... on MutationCreateMultipartUploadSuccess {
        data {
          uploadId
          uploadKey
          signedUrls {
            partNumber
            url
          }
        }
      }
    }
  }

  mutation AbortMultipartUpload($input: AbortMultipartUploadRequest!) {
    abortMultipartUpload(input: $input) {
      __typename
      ... on AwsRequestError {
        requestId
        message
      }
      ... on MutationAbortMultipartUploadSuccess {
        data {
          __typename
          uploadKey
        }
      }
    }
  }

  mutation CompleteMultipartUpload($input: CompleteMultipartUploadRequest!) {
    completeMultipartUpload(input: $input) {
      __typename
      ... on AwsRequestError {
        requestId
        message
      }
      ... on MutationCompleteMultipartUploadSuccess {
        data {
          uploadKey
        }
      }
    }
  }
`);

export async function uploadFileAndReturnKey(file: File, mediaType: MediaType): Promise<string> {
  if (IS_TEST) {
    return MOCK_TEST_S3;
  }

  const res = await fetchGQL(SignedUploadParamsDocument, {
    variables: { uploadRequest: { fileName: getS3SafeKeyName(file.name), mediaType } },
  });

  const params = res.data.signedUploadParams;
  if (!params) {
    throw new ApiDataError();
  }

  const s3Res = await uploadMediaToS3(file, params.url, params.fields);
  if (s3Res.status != 201) {
    // eslint-disable-next-line no-console
    console.error(s3Res);
    throw new FailedMediaUploadError();
  }

  return params.uploadKey;
}

// 10MB Chunk for Multipart Upload
const CHUNK_SIZE = 10_000_000;
const THROTTLE_INTERVAL = 200;

const SignPartReturn = z.object({
  url: z.string(),
});

const ProcessedParts = z.array(
  z.object({
    ETag: z.string(),
    PartNumber: z.number(),
  }),
);

export async function uploadMultipartFile({
  file,
  mediaType,
  setProgress,
}: {
  file: File;
  mediaType: MediaType;
  setProgress: (bytes: number) => void;
}) {
  const fileSize = file.size;
  const numChunks = Math.ceil(fileSize / CHUNK_SIZE);

  const createUploadData = await fetchGQL(CreateMultipartUploadDocument, {
    variables: {
      input: { fileName: getS3SafeKeyName(file.name), mediaType, totalPartsCount: numChunks },
    },
  });

  if (
    createUploadData.data?.createMultipartUpload.__typename !==
    'MutationCreateMultipartUploadSuccess'
  ) {
    throw new Error(
      createUploadData.data?.createMultipartUpload.message ?? 'Error creating upload',
    );
  }

  const urls = createUploadData.data?.createMultipartUpload?.data?.signedUrls.map(url => url.url);
  const uploadId = createUploadData.data?.createMultipartUpload?.data?.uploadId;
  const uploadKey = createUploadData.data?.createMultipartUpload?.data?.uploadKey;

  const uppy = new Uppy();

  uppy.use(AwsS3Multipart, {
    createMultipartUpload: () => Promise.resolve({ uploadId, key: uploadKey }),
    getChunkSize: () => CHUNK_SIZE,
    signPart: (_, partData) => {
      const partUrl = SignPartReturn.safeParse({ url: urls[partData.partNumber - 1] ?? '' });
      if (!partUrl.success) throw new Error('Invalid part URL');
      return partUrl.data;
    },
    completeMultipartUpload: async (_, { uploadId, key, parts }) => {
      const filteredParts = ProcessedParts.safeParse(
        parts.filter(part => !!part.ETag && !!part.PartNumber),
      );
      if (!filteredParts.success) throw new Error('Invalid parts');
      const processedParts = filteredParts.data
        .sort((partA, partB) => partA.PartNumber - partB.PartNumber)
        .map(part => ({
          etag: part.ETag,
          partNumber: part.PartNumber,
        }));

      const completeUploadData = await fetchGQL(CompleteMultipartUploadDocument, {
        variables: {
          input: {
            uploadId,
            uploadKey: key,
            parts: processedParts,
          },
        },
      });
      if (
        completeUploadData.data?.completeMultipartUpload.__typename !==
        'MutationCompleteMultipartUploadSuccess'
      ) {
        throw new Error(
          completeUploadData.data?.completeMultipartUpload.message ?? 'Error completing upload',
        );
      }
      return {};
    },
  });

  uppy.on(
    'upload-progress',
    throttle((_, progress) => {
      setProgress(progress.bytesUploaded);
    }, THROTTLE_INTERVAL),
  );

  uppy.addFile({ name: file.name, type: file.type, data: file });
  await uppy.upload();
  uppy.close();
  return uploadKey;
}

export function getS3SafeKeyName(name: string) {
  return name.replace(/[^0-9a-zA-Z!\-_\.\*\`]/gi, '_');
}
